import { useEffect, useState } from "react";
import {
  Activity,
  AlertOctagon,
  AlertTriangle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Flame,
  Info,
  Pause,
  Play,
  Sparkles,
  X
} from "lucide-react";
import type {
  WallBoard,
  WallComingGoingPanel,
  WallFloorCommandPanel,
  WallInsight,
  WallLateHotJobsPanel,
  WallOspDuePanel,
  WallProgramLobPanel,
  WallSqdcPanel,
  WallTickerItem
} from "../../api/parityApi";
import { usePrincipal } from "../../auth/AuthContext";
import { isWallDisplayOnly } from "../../auth/roles";
import { Ticker } from "../ticker/Ticker";
import { ValueKindBadge } from "../ValueKindBadge";

const PANEL_LABELS: Record<string, string> = {
  "floor-command": "Floor Command",
  sqdc: "SQDC Board",
  "late-hot-jobs": "Late & Hot Jobs",
  "osp-due": "OSP Due",
  "program-lob": "Program LOB",
  "coming-going": "Coming & Going"
};

function detectReducedMotion(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return Boolean(window.matchMedia("(prefers-reduced-motion: reduce)")?.matches);
}

function AggregateWallTicker({ items, reducedMotion }: { items: WallTickerItem[]; reducedMotion: boolean }) {
  const [paused, setPaused] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (paused || items.length <= 1) return;
    const timer = window.setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % items.length);
    }, 6_000);
    return () => window.clearInterval(timer);
  }, [paused, items.length]);

  if (items.length === 0) return null;
  const active = items[activeIndex] ?? items[0]!;

  return (
    <div
      role="region"
      aria-label="Plant operations wall ticker"
      aria-live="off"
      data-testid="ticker-wall"
      data-paused={paused ? "true" : "false"}
      data-reduced-motion={reducedMotion ? "true" : "false"}
      className="relative flex min-w-0 items-center justify-between gap-4 rounded-xl border border-viper-cyan/40 bg-brand-900/95 px-5 py-3 text-xl text-white shadow-lg"
    >
      <div className="flex min-w-0 flex-1 items-center gap-3 overflow-hidden" data-testid="wall-ticker-active-item">
        <span
          className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1 text-sm font-bold uppercase tracking-wide ${
            active.severity === "critical"
              ? "border-red-400/60 bg-red-950/90 text-red-200"
              : active.severity === "attention"
                ? "border-amber-400/60 bg-amber-950/90 text-amber-200"
                : active.severity === "good-news"
                  ? "border-emerald-400/60 bg-emerald-950/90 text-emerald-200"
                  : "border-viper-cyan/60 bg-slate-900 text-viper-cyan"
          }`}
        >
          {active.severity === "critical" ? (
            <AlertOctagon aria-hidden="true" className="h-4 w-4" />
          ) : active.severity === "attention" ? (
            <AlertTriangle aria-hidden="true" className="h-4 w-4" />
          ) : active.severity === "good-news" ? (
            <CheckCircle2 aria-hidden="true" className="h-4 w-4" />
          ) : (
            <Info aria-hidden="true" className="h-4 w-4" />
          )}
          <span>{active.severityLabel}</span>
        </span>
        <span className="truncate font-semibold tracking-tight text-slate-50">{active.text}</span>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        <span className="text-xs font-medium text-slate-300 tabular-nums">
          {activeIndex + 1} / {items.length}
        </span>
        <button
          type="button"
          onClick={() => setPaused((p) => !p)}
          aria-label={paused ? "Resume wall ticker" : "Pause wall ticker"}
          aria-pressed={paused}
          data-testid="wall-ticker-pause-btn"
          className="inline-flex items-center gap-1.5 rounded-lg border border-white/25 bg-white/10 px-3 py-1.5 text-sm font-semibold text-white hover:bg-white/20"
        >
          {paused ? <Play aria-hidden="true" className="h-4 w-4" /> : <Pause aria-hidden="true" className="h-4 w-4" />}
          <span>{paused ? "Resume" : "Pause"}</span>
        </button>
      </div>
    </div>
  );
}

function WallInsightBanner({ insight }: { insight: WallInsight | undefined }) {
  if (!insight) return null;
  return (
    <aside
      aria-label="VIPER panel insight"
      data-testid="wall-insight-strip"
      data-ai={insight.aiWritten ? "true" : "false"}
      className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-viper-cyan/35 bg-slate-900/90 px-3 py-3 text-slate-50 shadow-md sm:px-5"
    >
      <div className="flex w-full min-w-0 flex-1 flex-col items-start gap-2 sm:w-auto sm:flex-row sm:items-center sm:gap-3">
        <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-viper-cyan/40 bg-viper-navy px-3 py-1 text-xs font-bold uppercase tracking-wider text-viper-cyan">
          <Sparkles aria-hidden="true" className="h-3.5 w-3.5" />
          <span>{insight.label}</span>
        </span>
        <p className="w-full min-w-0 flex-1 break-words text-lg font-medium leading-snug text-slate-100 sm:w-auto" data-testid="wall-insight-text">
          {insight.text}
          {insight.watch && (
            <span className="mt-1 block font-semibold text-amber-300 sm:ml-3 sm:mt-0 sm:inline">Watch: {insight.watch}</span>
          )}
        </p>
      </div>
      <ValueKindBadge kind={insight.valueKind} compact />
    </aside>
  );
}

export function WallDisplayBoardView({
  board,
  selectedDisplayKey,
  onSelectDisplayKey
}: {
  board: WallBoard;
  selectedDisplayKey: string;
  onSelectDisplayKey: (key: string) => void;
}) {
  const principal = usePrincipal();
  const roles = principal?.roles ?? [];
  const isKioskOnly = !principal || roles.length === 0 || isWallDisplayOnly(roles);

  const panels = board.activePanels.length > 0 ? board.activePanels : ["floor-command", "sqdc", "late-hot-jobs", "osp-due", "program-lob"];
  const [activePanelIndex, setActivePanelIndex] = useState(0);
  const [rotationPaused, setRotationPaused] = useState(false);
  const [reducedMotion, setReducedMotion] = useState<boolean>(() => detectReducedMotion());
  const [detailOverlay, setDetailOverlay] = useState<{ title: string; subtitle: string; items: string[] } | null>(null);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (!mq) return;
    const onChange = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    setReducedMotion(mq.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  const currentPanel = panels[activePanelIndex % panels.length] ?? panels[0]!;
  const effectivePaused = rotationPaused || detailOverlay !== null;

  useEffect(() => {
    if (effectivePaused || panels.length <= 1) return;
    const dwellMs = Math.max(8, board.dwellSeconds || 15) * 1000;
    const timer = window.setInterval(() => {
      setActivePanelIndex((prev) => (prev + 1) % panels.length);
    }, dwellMs);
    return () => window.clearInterval(timer);
  }, [effectivePaused, panels.length, board.dwellSeconds]);

  const activeInsight = board.panelInsights[currentPanel];

  return (
    <div className="flex w-full min-w-0 flex-1 flex-col gap-4 px-3 py-4 text-left sm:px-6" data-testid="wall-board-active">
      {/* Top Wall Ticker: uses <Ticker variant="wall" /> for previewers and AggregateWallTicker for kiosk role */}
      {isKioskOnly ? (
        <AggregateWallTicker items={board.ticker.items} reducedMotion={reducedMotion} />
      ) : (
        <div className="space-y-2">
          <Ticker variant="wall" />
          {/* Fallback aggregate wall ticker if /api/v1/ticker has not resolved yet */}
          <AggregateWallTicker items={board.ticker.items} reducedMotion={reducedMotion} />
        </div>
      )}

      {/* Board control bar: display switcher, rotating panel tabs, and rotation pause/step controls */}
      <div
        className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-white/15 bg-slate-900/85 px-4 py-2.5"
        data-testid="wall-controls-bar"
      >
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded bg-viper-cyan/20 px-2.5 py-1 text-xs font-bold uppercase tracking-wider text-viper-cyan">
            {board.boardKind} · {board.scopeLabel}
          </span>
          <span className="text-base font-semibold text-slate-100" data-testid="wall-board-title">
            {board.title}
          </span>
          {board.availableDisplays.length > 1 && (
            <div className="ml-2 inline-flex items-center gap-1 rounded-lg border border-white/15 bg-viper-navy p-1" role="group" aria-label="Switch wall display">
              {board.availableDisplays.map((d) => (
                <button
                  key={d.key}
                  type="button"
                  onClick={() => {
                    onSelectDisplayKey(d.key);
                    setActivePanelIndex(0);
                  }}
                  data-testid={`wall-switch-${d.key}`}
                  aria-pressed={d.key === selectedDisplayKey}
                  className={`rounded px-2.5 py-1 text-xs font-semibold transition-colors ${
                    d.key === selectedDisplayKey
                      ? "bg-viper-cyan text-viper-navy"
                      : "text-slate-200 hover:bg-white/10"
                  }`}
                >
                  {d.key}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Panel selector pills + Play/Pause/Prev/Next */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex flex-wrap items-center gap-1" role="tablist" aria-label="Wall display rotating panels">
            {panels.map((pKey, idx) => {
              const active = idx === activePanelIndex % panels.length;
              return (
                <button
                  key={pKey}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  data-testid={`wall-panel-tab-${pKey}`}
                  onClick={() => setActivePanelIndex(idx)}
                  className={`rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors ${
                    active
                      ? "bg-brand-600 text-white shadow-sm"
                      : "bg-white/10 text-slate-200 hover:bg-white/20"
                  }`}
                >
                  {PANEL_LABELS[pKey] ?? pKey}
                </button>
              );
            })}
          </div>

          <div className="flex items-center gap-1 border-l border-white/15 pl-2">
            <button
              type="button"
              aria-label="Previous panel"
              data-testid="wall-prev-panel"
              onClick={() => setActivePanelIndex((prev) => (prev - 1 + panels.length) % panels.length)}
              className="rounded-lg border border-white/20 bg-white/10 p-1.5 text-white hover:bg-white/20"
            >
              <ChevronLeft aria-hidden="true" className="h-4 w-4" />
            </button>
            <button
              type="button"
              aria-label={rotationPaused ? "Resume panel rotation" : "Pause panel rotation"}
              aria-pressed={rotationPaused}
              data-testid="wall-pause-rotation"
              onClick={() => setRotationPaused((p) => !p)}
              className="inline-flex items-center gap-1 rounded-lg border border-white/20 bg-white/10 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-white/20"
            >
              {rotationPaused ? <Play aria-hidden="true" className="h-3.5 w-3.5" /> : <Pause aria-hidden="true" className="h-3.5 w-3.5" />}
              <span>{rotationPaused ? "Paused" : `Rotate (${board.dwellSeconds}s)`}</span>
            </button>
            <button
              type="button"
              aria-label="Next panel"
              data-testid="wall-next-panel"
              onClick={() => setActivePanelIndex((prev) => (prev + 1) % panels.length)}
              className="rounded-lg border border-white/20 bg-white/10 p-1.5 text-white hover:bg-white/20"
            >
              <ChevronRight aria-hidden="true" className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Active Panel VIPER Insight Strip */}
      <WallInsightBanner insight={activeInsight} />

      {/* Active Rotating Panel Body (3m-readable large type, aggregate-only) */}
      <div className="min-h-0 flex-1" data-testid={`wall-panel-${currentPanel}`}>
        {currentPanel === "floor-command" && board.floorCommand && (
          <WallFloorCommandView
            panel={board.floorCommand}
            onOpenDetail={(title, subtitle, items) => setDetailOverlay({ title, subtitle, items })}
          />
        )}
        {currentPanel === "sqdc" && board.sqdc && (
          <WallSqdcView
            panel={board.sqdc}
            onOpenDetail={(title, subtitle, items) => setDetailOverlay({ title, subtitle, items })}
          />
        )}
        {currentPanel === "late-hot-jobs" && board.lateHotJobs && (
          <WallLateHotJobsView
            panel={board.lateHotJobs}
            onOpenDetail={(title, subtitle, items) => setDetailOverlay({ title, subtitle, items })}
          />
        )}
        {currentPanel === "osp-due" && board.ospDue && (
          <WallOspDueView
            panel={board.ospDue}
            onOpenDetail={(title, subtitle, items) => setDetailOverlay({ title, subtitle, items })}
          />
        )}
        {currentPanel === "program-lob" && board.programLob && (
          <WallProgramLobView
            panel={board.programLob}
            onOpenDetail={(title, subtitle, items) => setDetailOverlay({ title, subtitle, items })}
          />
        )}
        {currentPanel === "coming-going" && board.comingGoing && (
          <WallComingGoingView panel={board.comingGoing} />
        )}
      </div>

      {/* Touch / Click Detail Overlay (never leaves the board) */}
      {detailOverlay && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-6"
          data-testid="wall-detail-overlay"
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="wall-overlay-heading"
            className="w-full max-w-3xl rounded-2xl border border-viper-cyan/40 bg-brand-900 p-6 text-slate-50 shadow-2xl"
          >
            <div className="flex items-start justify-between gap-4 border-b border-white/15 pb-4">
              <div>
                <h2 id="wall-overlay-heading" className="text-2xl font-bold text-white">
                  {detailOverlay.title}
                </h2>
                <p className="mt-1 text-base text-slate-300">{detailOverlay.subtitle}</p>
              </div>
              <button
                type="button"
                aria-label="Close detail overlay"
                data-testid="wall-detail-overlay-close"
                onClick={() => setDetailOverlay(null)}
                className="rounded-full border border-white/30 bg-white/10 p-2 text-white hover:bg-white/20"
              >
                <X aria-hidden="true" className="h-6 w-6" />
              </button>
            </div>
            <ul className="mt-4 space-y-2 text-lg text-slate-100">
              {detailOverlay.items.map((line, i) => (
                <li key={i} className="rounded-lg border border-white/10 bg-slate-900/80 px-4 py-2.5">
                  {line}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

function WallFloorCommandView({
  panel,
  onOpenDetail
}: {
  panel: WallFloorCommandPanel;
  onOpenDetail: (title: string, subtitle: string, items: string[]) => void;
}) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="rounded-xl border border-emerald-400/40 bg-slate-900/90 p-4">
          <div className="text-sm font-semibold uppercase tracking-wider text-emerald-300">Running Workcenters</div>
          <div className="mt-1 text-4xl font-bold tabular-nums text-white">{panel.runningCount}</div>
        </div>
        <div className="rounded-xl border border-amber-400/40 bg-slate-900/90 p-4">
          <div className="text-sm font-semibold uppercase tracking-wider text-amber-300">Overloaded</div>
          <div className="mt-1 text-4xl font-bold tabular-nums text-white">{panel.overloadedCount}</div>
        </div>
        <div className="rounded-xl border border-red-400/40 bg-slate-900/90 p-4">
          <div className="text-sm font-semibold uppercase tracking-wider text-red-300">Down / Maintenance</div>
          <div className="mt-1 text-4xl font-bold tabular-nums text-white">{panel.downCount}</div>
        </div>
        <div className="rounded-xl border border-viper-cyan/40 bg-slate-900/90 p-4">
          <div className="text-sm font-semibold uppercase tracking-wider text-viper-cyan">Operators Clocked In</div>
          <div className="mt-1 text-4xl font-bold tabular-nums text-white" data-testid="wall-total-operators">
            {panel.totalActiveOperators}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="wall-wc-tiles">
        {panel.workcenters.slice(0, 8).map((wc) => (
          <button
            key={wc.workcenterCode}
            type="button"
            data-testid={`wall-wc-tile-${wc.workcenterCode}`}
            onClick={() =>
              onOpenDetail(
                `${wc.workcenterCode} · ${wc.workcenterName}`,
                `Shop ${wc.shopCode ?? "Plant"} · ${wc.statusLabel}`,
                [
                  `Aggregate staffing: ${wc.operatorAggregateLabel} (no personal names on shared screens)`,
                  `Ready operations: ${wc.readyOperationCount} · Queued operations: ${wc.queuedOperationCount}`,
                  `Remaining load: ${wc.remainingHours} h (${wc.utilisationPercent ?? 0}% utilisation)`
                ]
              )
            }
            className={`flex flex-col justify-between gap-2 rounded-xl border p-4 text-left transition-colors hover:bg-slate-800/90 ${
              wc.status === "down"
                ? "border-red-400/70 bg-red-950/60"
                : wc.status === "overloaded"
                  ? "border-amber-400/70 bg-amber-950/50"
                  : wc.status === "running"
                    ? "border-emerald-400/60 bg-slate-900/90"
                    : "border-white/20 bg-slate-900/80"
            }`}
          >
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-xl font-bold text-white">{wc.workcenterCode}</span>
                  {wc.isBottleneck && (
                    <span className="inline-flex items-center gap-1 rounded bg-red-900/90 px-2 py-0.5 text-xs font-bold text-red-100">
                      <Flame aria-hidden="true" className="h-3.5 w-3.5" />
                      Drum
                    </span>
                  )}
                </div>
                <div className="text-sm text-slate-300">{wc.workcenterName}</div>
              </div>
              <span className="inline-flex items-center gap-1 rounded-full border border-white/25 bg-white/10 px-2.5 py-1 text-xs font-bold text-white">
                <Activity aria-hidden="true" className="h-3.5 w-3.5" />
                <span>{wc.statusLabel}</span>
              </span>
            </div>

            <div className="mt-2 grid grid-cols-3 gap-2 border-t border-white/10 pt-2 text-sm">
              <div>
                <div className="text-xs text-slate-300">Staffing</div>
                <div className="text-lg font-bold tabular-nums text-white">{wc.activeOperatorCount} ops</div>
              </div>
              <div>
                <div className="text-xs text-slate-300">Ready / Q</div>
                <div className="text-lg font-bold tabular-nums text-white">
                  {wc.readyOperationCount} / {wc.queuedOperationCount}
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-300">Load</div>
                <div className="text-lg font-bold tabular-nums text-white">
                  {wc.utilisationPercent !== null ? `${wc.utilisationPercent}%` : `${wc.remainingHours}h`}
                </div>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function WallSqdcView({
  panel,
  onOpenDetail
}: {
  panel: WallSqdcPanel;
  onOpenDetail: (title: string, subtitle: string, items: string[]) => void;
}) {
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2" data-testid="wall-sqdc-grid">
      {panel.tiles.map((t) => (
        <button
          key={t.letter}
          type="button"
          data-testid={`wall-sqdc-tile-${t.letter}`}
          onClick={() =>
            onOpenDetail(`${t.initial} — ${t.title}`, panel.periodLabel, [
              `Current figure: ${t.formattedValue} (Target: ${t.formattedTarget})`,
              `Status: ${t.statusLabel} (${t.formattedDelta})`,
              t.secondarySummary
            ])
          }
          className="flex flex-col justify-between gap-3 rounded-2xl border border-white/20 bg-slate-900/90 p-5 text-left hover:bg-slate-800/90"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-700 text-3xl font-extrabold text-white">
                {t.initial}
              </span>
              <div>
                <div className="text-2xl font-bold text-white">{t.title}</div>
                <div className="text-sm text-slate-300">Target {t.formattedTarget}</div>
              </div>
            </div>
            <span
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm font-bold ${
                t.targetState === "met"
                  ? "border-emerald-400/60 bg-emerald-950/90 text-emerald-200"
                  : t.targetState === "near"
                    ? "border-amber-400/60 bg-amber-950/90 text-amber-200"
                    : "border-red-400/60 bg-red-950/90 text-red-200"
              }`}
            >
              {t.targetState === "met" ? (
                <CheckCircle2 aria-hidden="true" className="h-4 w-4" />
              ) : t.targetState === "near" ? (
                <AlertTriangle aria-hidden="true" className="h-4 w-4" />
              ) : (
                <AlertOctagon aria-hidden="true" className="h-4 w-4" />
              )}
              <span>{t.statusLabel}</span>
            </span>
          </div>

          <div className="flex items-baseline justify-between gap-4 border-t border-white/10 pt-3">
            <span className="text-4xl font-extrabold tabular-nums text-white">{t.formattedValue}</span>
            <span className="text-base font-medium text-slate-300">{t.formattedDelta}</span>
          </div>

          <div className="text-sm text-slate-300">{t.secondarySummary}</div>
        </button>
      ))}
    </div>
  );
}

function WallLateHotJobsView({
  panel,
  onOpenDetail
}: {
  panel: WallLateHotJobsPanel;
  onOpenDetail: (title: string, subtitle: string, items: string[]) => void;
}) {
  return (
    <div className="space-y-4" data-testid="wall-late-hot-jobs-view">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="rounded-xl border border-red-400/50 bg-slate-900/90 p-4">
          <div className="text-sm font-semibold uppercase text-red-300">Active Hot Jobs</div>
          <div className="mt-1 text-4xl font-bold tabular-nums text-white">
            {panel.activeHotCount} / {panel.hotPriorityCap}
          </div>
        </div>
        <div className="rounded-xl border border-amber-400/50 bg-slate-900/90 p-4">
          <div className="text-sm font-semibold uppercase text-amber-300">Late Hot Jobs</div>
          <div className="mt-1 text-4xl font-bold tabular-nums text-white">{panel.lateHotCount}</div>
        </div>
        <div className="rounded-xl border border-red-400/50 bg-slate-900/90 p-4">
          <div className="text-sm font-semibold uppercase text-red-300">Critical Top Levels</div>
          <div className="mt-1 text-4xl font-bold tabular-nums text-white">{panel.criticalTopLevelCount}</div>
        </div>
        <div className="rounded-xl border border-white/20 bg-slate-900/90 p-4">
          <div className="text-sm font-semibold uppercase text-slate-300">On Hold in GSS</div>
          <div className="mt-1 text-4xl font-bold tabular-nums text-white">{panel.onHoldCount}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
        {panel.jobs.slice(0, 6).map((j) => (
          <button
            key={j.workOrderKey}
            type="button"
            onClick={() =>
              onOpenDetail(`Work Order ${j.displayWorkOrder}`, `Part ${j.partNumber} · Shop ${j.shopCode ?? "Plant"}`, [
                `Priority: ${j.priority} · Health Tier: ${j.healthTier}`,
                `Due Date: ${j.dueDate ?? "—"} · Forecast Finish: ${j.forecastFinishDate ?? "—"}`,
                `Variance: ${j.varianceWorkdays ?? 0} workdays · Stage: ${j.wipStage}`
              ])
            }
            className="flex items-center justify-between gap-3 rounded-xl border border-white/20 bg-slate-900/90 px-4 py-3 text-left hover:bg-slate-800/90"
          >
            <div>
              <div className="flex items-center gap-2">
                <span className="font-mono text-xl font-bold text-white">{j.displayWorkOrder}</span>
                <span className="rounded bg-red-900/80 px-2 py-0.5 text-xs font-bold text-red-100">{j.priority}</span>
              </div>
              <div className="text-sm text-slate-300">
                Part {j.partNumber} · Shop {j.shopCode ?? "Plant"} · Due {j.dueDate ?? "—"}
              </div>
            </div>
            <div className="text-right">
              <div className="text-xl font-bold tabular-nums text-amber-300">
                {(j.varianceWorkdays ?? 0) > 0 ? `+${j.varianceWorkdays} d late` : `${j.varianceWorkdays ?? 0} d`}
              </div>
              <div className="text-xs font-semibold text-slate-300">{j.healthTier}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function WallOspDueView({
  panel,
  onOpenDetail
}: {
  panel: WallOspDuePanel;
  onOpenDetail: (title: string, subtitle: string, items: string[]) => void;
}) {
  return (
    <div className="space-y-4" data-testid="wall-osp-due-view">
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-xl border border-white/20 bg-slate-900/90 p-4">
          <div className="text-sm font-semibold uppercase text-slate-300">Open OSP Lines</div>
          <div className="mt-1 text-4xl font-bold tabular-nums text-white">{panel.openOspCount}</div>
        </div>
        <div className="rounded-xl border border-red-400/50 bg-slate-900/90 p-4">
          <div className="text-sm font-semibold uppercase text-red-300">Overdue at Vendor</div>
          <div className="mt-1 text-4xl font-bold tabular-nums text-white">{panel.overdueAtVendorCount}</div>
        </div>
        <div className="rounded-xl border border-viper-cyan/40 bg-slate-900/90 p-4">
          <div className="text-sm font-semibold uppercase text-viper-cyan">Avg Turnaround</div>
          <div className="mt-1 text-4xl font-bold tabular-nums text-white">{panel.averageTurnaroundWorkdays} d</div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
        {panel.items.slice(0, 6).map((item) => (
          <button
            key={item.key}
            type="button"
            onClick={() =>
              onOpenDetail(`OSP ${item.workOrderKey} · Part ${item.partNumber}`, item.processDescription, [
                `Vendor code: ${item.vendorCode} · PO: ${item.purchaseOrder ?? "Pending"}`,
                `Promised return date: ${item.dueDate ?? "—"} (${item.statusLabel})`
              ])
            }
            className="flex items-center justify-between gap-3 rounded-xl border border-white/20 bg-slate-900/90 px-4 py-3 text-left hover:bg-slate-800/90"
          >
            <div>
              <div className="text-lg font-bold text-white">
                {item.workOrderKey} · {item.partNumber}
              </div>
              <div className="text-sm text-slate-300">
                {item.processDescription} · Vendor {item.vendorCode}
              </div>
            </div>
            <span className="rounded-full border border-amber-400/50 bg-amber-950/80 px-3 py-1 text-sm font-bold text-amber-200">
              {item.statusLabel}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

function WallProgramLobView({
  panel,
  onOpenDetail
}: {
  panel: WallProgramLobPanel;
  onOpenDetail: (title: string, subtitle: string, items: string[]) => void;
}) {
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2" data-testid="wall-program-lob-view">
      {panel.programs.slice(0, 4).map((p) => (
        <button
          key={p.programCode}
          type="button"
          onClick={() =>
            onOpenDetail(`${p.programCode} — ${p.programName}`, `${p.division} Division · ${p.overallStatusLabel}`, [
              `Planned at status date: ${p.plannedAtStatusDate} units · Actual shipped: ${p.actualAtStatusDate} units`,
              `Open work orders: ${p.openWorkOrderCount} (${p.lateWorkOrderCount} late)`,
              ...p.controlPoints.map(
                (cp) => `${cp.name} (-${cp.leadTimeOffsetWorkdays}d): ${cp.actualCompletedQuantity} actual vs ${cp.lineOfBalanceQuantity} LOB`
              )
            ])
          }
          className="flex flex-col justify-between gap-3 rounded-2xl border border-white/20 bg-slate-900/90 p-5 text-left hover:bg-slate-800/90"
        >
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="text-2xl font-bold text-white">
                {p.programCode} · {p.programName}
              </div>
              <div className="text-sm text-slate-300">
                Division: {p.division} · Open WOs: {p.openWorkOrderCount}
              </div>
            </div>
            <span className="rounded-full border border-viper-cyan/40 bg-viper-navy px-3 py-1 text-xs font-bold text-viper-cyan">
              {p.overallStatusLabel}
            </span>
          </div>

          <div className="grid grid-cols-3 gap-2 border-t border-white/10 pt-3 text-base">
            <div>
              <div className="text-xs text-slate-300">Planned Cum.</div>
              <div className="text-2xl font-bold tabular-nums text-white">{p.plannedAtStatusDate} u</div>
            </div>
            <div>
              <div className="text-xs text-slate-300">Shipped Cum.</div>
              <div className="text-2xl font-bold tabular-nums text-emerald-300">{p.actualAtStatusDate} u</div>
            </div>
            <div>
              <div className="text-xs text-slate-300">Delivery Gap</div>
              <div className="text-2xl font-bold tabular-nums text-amber-300">{p.deliveryGap} u</div>
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

function WallComingGoingView({ panel }: { panel: WallComingGoingPanel }) {
  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4" data-testid="wall-coming-going-view">
      <div className="flex flex-col gap-2 rounded-2xl border border-white/20 bg-slate-900/90 p-5 text-white">
        <div className="flex items-center justify-between gap-2 text-lg font-semibold text-slate-200">
          <span>Going out today</span>
          <ValueKindBadge kind="computed" compact />
        </div>
        <div className="text-6xl font-bold tabular-nums">{panel.goingOutTodayCount}</div>
        <div className="text-base text-slate-300">
          {panel.goingByCarrier.length > 0
            ? panel.goingByCarrier.map((c) => `${c.carrier} ×${c.count}${c.pickup ? ` (pick-up ${c.pickup})` : ""}`).join(" · ")
            : "Nothing going out today."}
        </div>
      </div>

      <div className="flex flex-col gap-2 rounded-2xl border border-white/20 bg-slate-900/90 p-5 text-white">
        <div className="flex items-center justify-between gap-2 text-lg font-semibold text-slate-200">
          <span>Coming in today</span>
          <ValueKindBadge kind="computed" compact />
        </div>
        <div className="text-6xl font-bold tabular-nums">{panel.comingInTodayCount}</div>
        <div className="text-base text-slate-300">
          {panel.comingOspReturnsCount} OSP returns · {panel.comingPoReceiptsCount} PO receipts
        </div>
      </div>

      <div className="flex flex-col gap-2 rounded-2xl border border-white/20 bg-slate-900/90 p-5 text-white">
        <div className="flex items-center justify-between gap-2 text-lg font-semibold text-slate-200">
          <span>Exceptions</span>
          <ValueKindBadge kind="computed" compact />
        </div>
        <div className="text-6xl font-bold tabular-nums">{panel.exceptionsCount}</div>
        <div className="text-base text-slate-300">
          {panel.exceptionsCount > 0 ? "Need a person today" : "All moving normally"}
        </div>
      </div>

      <div className="flex flex-col gap-2 rounded-2xl border border-white/20 bg-slate-900/90 p-5 text-white">
        <div className="flex items-center justify-between gap-2 text-lg font-semibold text-slate-200">
          <span>Late</span>
          <ValueKindBadge kind="computed" compact />
        </div>
        <div className="text-6xl font-bold tabular-nums">{panel.lateCount}</div>
        <div className="text-base text-slate-300">
          This week: {panel.weekGoingCount} going · {panel.weekComingCount} coming
        </div>
      </div>
    </div>
  );
}
